import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  Archive,
  Trash2,
  Pencil,
  X,
} from 'lucide-react';
import { CRMUser, Lead, LeadStatus } from '../../types';
import LeadStatusPill from './LeadStatusPill';
import { LEAD_STATUSES, LEAD_STATUS_LABELS } from './leadStatusDisplay';
import { getSourceColor } from '../../utils/sourceColors';
import { getScoreBandColor, getScoreBandLabel } from '../../utils/enrichmentService';
import { formatSourceName } from '../../utils/sourceUtils';
import {
  formatDateSafe,
  formatLastUpdated,
  getUserInitials,
  LeadsSortColumn,
} from '../../utils/leadsHubUtils';
import type { LeadLastUpdatedInfo } from '../../utils/leadsHubUtils';
import './LeadsTable.css';

interface LeadsTableProps {
  leads: Lead[];
  totalCount: number;
  crmUsers: CRMUser[];
  lastUpdatedByLead: Map<string, LeadLastUpdatedInfo>;
  hasLoggedCallByLead: Map<string, boolean>;
  sortBy: LeadsSortColumn;
  sortOrder: 'asc' | 'desc';
  onSort: (column: LeadsSortColumn) => void;
  selectedIds: Set<string>;
  onToggleSelect: (leadId: string) => void;
  onToggleSelectAll: () => void;
  onClearSelection: () => void;
  onBulkStatus: (status: LeadStatus) => Promise<void>;
  onLeadStatusChange: (leadId: string, status: LeadStatus) => Promise<void>;
  onBulkArchive: (archive: boolean) => Promise<void>;
  onBulkDelete: () => Promise<void>;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  totalCount,
  crmUsers,
  lastUpdatedByLead,
  hasLoggedCallByLead,
  sortBy,
  sortOrder,
  onSort,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  onBulkStatus,
  onLeadStatusChange,
  onBulkArchive,
  onBulkDelete,
}) => {
  const navigate = useNavigate();
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<LeadStatus | ''>('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const visibleIds = useMemo(() => leads.map(l => l.id).filter((id): id is string => !!id), [leads]);
  const isAllSelected = visibleIds.length > 0 && selectedIds.size === visibleIds.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < visibleIds.length;

  const getUserById = useCallback(
    (uid: string | undefined) => (uid ? crmUsers.find(u => u.uid === uid) : undefined),
    [crmUsers],
  );

  const SortIndicator = ({ column }: { column: LeadsSortColumn }) => {
    if (sortBy !== column) return <span className="leads-sort-indicator">⇅</span>;
    return <span className="leads-sort-indicator active">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleRowClick = (lead: Lead) => {
    if (lead.id) navigate(`/client/${lead.id}`);
  };

  const handleBulkApply = async () => {
    if (!bulkStatus) return;
    setBulkUpdating(true);
    try {
      await onBulkStatus(bulkStatus);
      setShowBulkModal(false);
      setBulkStatus('');
    } finally {
      setBulkUpdating(false);
    }
  };

  const sortableColumns: { id: LeadsSortColumn; label: string; className?: string }[] = [
    { id: 'name', label: 'Name', className: 'leads-td-name' },
    { id: 'status', label: 'Status' },
    { id: 'source', label: 'Source' },
    { id: 'location', label: 'Location' },
    { id: 'addedBy', label: 'Owner' },
    { id: 'lastUpdated', label: 'Last Updated' },
    { id: 'created', label: 'Created' },
  ];

  return (
    <>
      {selectedIds.size > 0 && (
        <div className="leads-bulk-bar" data-tour="leads-bulk-bar">
          <span className="leads-bulk-info">
            {selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="leads-bulk-actions">
            <button type="button" className="leads-bulk-btn" onClick={() => setShowBulkModal(true)}>
              <Pencil size={14} /> Edit status
            </button>
            <button type="button" className="leads-bulk-btn" onClick={() => onBulkArchive(true)}>
              <Archive size={14} /> Archive
            </button>
            <button type="button" className="leads-bulk-btn" onClick={() => onBulkArchive(false)}>
              Unarchive
            </button>
            <button type="button" className="leads-bulk-btn danger" onClick={onBulkDelete}>
              <Trash2 size={14} /> Delete
            </button>
            <button type="button" className="leads-bulk-btn" onClick={onClearSelection}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="leads-table-wrap pm-scroll" data-tour="leads-table">
        <div className="leads-table-scroll">
          <table className="leads-table">
            <thead>
              <tr>
                <th className="leads-th leads-th-check">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={el => {
                      if (el) el.indeterminate = isSomeSelected;
                    }}
                    onChange={onToggleSelectAll}
                    aria-label="Select all leads"
                  />
                </th>
                {sortableColumns.map(col => (
                  <th
                    key={col.id}
                    className="leads-th sortable"
                    onClick={() => onSort(col.id)}
                  >
                    {col.label} <SortIndicator column={col.id} />
                  </th>
                ))}
                <th className="leads-th">Signals</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className="leads-empty">
                      <p>No leads match your filters</p>
                      <span>Try adjusting sidebar filters or press Space for quick search</span>
                    </div>
                  </td>
                </tr>
              ) : (
                leads.map(lead => {
                  const lastUpdated = lead.id ? lastUpdatedByLead.get(lead.id) : undefined;
                  return (
                    <tr
                      key={lead.id}
                      className={`leads-row ${lead.archived ? 'archived' : ''} ${lead.id && selectedIds.has(lead.id) ? 'selected' : ''}`}
                      onClick={() => handleRowClick(lead)}
                    >
                      <td className="leads-td leads-td-check" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={lead.id ? selectedIds.has(lead.id) : false}
                          onChange={() => lead.id && onToggleSelect(lead.id)}
                          aria-label={`Select ${lead.firstName} ${lead.lastName}`}
                        />
                      </td>
                      <td className="leads-td leads-td-name">
                        <div className="leads-name-cell">
                          <span className="leads-name-primary">
                            {lead.firstName} {lead.lastName}
                          </span>
                          {lead.company && <span className="leads-name-company">{lead.company}</span>}
                        </div>
                      </td>
                      <td className="leads-td">
                        {lead.id ? (
                          <LeadStatusPill
                            status={lead.status}
                            onChange={status => onLeadStatusChange(lead.id!, status)}
                          />
                        ) : (
                          <LeadStatusPill status={lead.status} readonly />
                        )}
                      </td>
                      <td className="leads-td">
                        <span
                          className="leads-source-pill"
                          style={{ backgroundColor: getSourceColor(lead.source) }}
                        >
                          {formatSourceName(lead.source)}
                        </span>
                      </td>
                      <td className="leads-td leads-td-muted">{lead.location || '—'}</td>
                      <td className="leads-td leads-td-owner">
                        {getUserInitials(getUserById(lead.addedBy)?.displayName, lead.addedBy)}
                      </td>
                      <td className="leads-td leads-td-muted">
                        <span
                          className="leads-last-updated"
                          title={lastUpdated ? `${lastUpdated.label} · ${formatDateSafe(lastUpdated.at, 'MMM d, yyyy h:mm a')}` : undefined}
                        >
                          {formatLastUpdated(lastUpdated)}
                        </span>
                      </td>
                      <td className="leads-td leads-td-muted">
                        {formatDateSafe(lead.createdAt, 'MMM d, yyyy')}
                      </td>
                      <td className="leads-td">
                        <span className="leads-indicators">
                          {lead.id && hasLoggedCallByLead.get(lead.id) && (
                            <span className="leads-indicator call" title="Call logged">
                              <Phone size={12} />
                            </span>
                          )}
                          {lead.hasUnreadMessage && (
                            <span className="leads-indicator unread" title="Unread message" />
                          )}
                          {lead.metricsLiteId && (
                            <span className="leads-indicator ml" title="Metrics Lite tracking">
                              ML
                            </span>
                          )}
                          {lead.enrichment?.score && (
                            <span
                              className="leads-score-badge"
                              title={`${getScoreBandLabel(lead.enrichment.score.band)} (${lead.enrichment.score.total})`}
                              style={{ background: getScoreBandColor(lead.enrichment.score.band) }}
                            >
                              {lead.enrichment.score.total}
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="leads-table-footer">
        <span>
          Showing {leads.length} of {totalCount} leads
          {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
        </span>
        <span className="leads-table-footer-hint">Space — quick search</span>
      </div>

      {showBulkModal && (
        <div className="ops-modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="ops-modal" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="bulk-edit-title">
            <div className="ops-modal-header">
              <h2 id="bulk-edit-title">Edit {selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''}</h2>
              <button type="button" className="ops-modal-close" onClick={() => setShowBulkModal(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="ops-modal-body">
              <label className="ops-modal-label" htmlFor="bulk-status">Status</label>
              <select
                id="bulk-status"
                className="ops-modal-input"
                value={bulkStatus}
                onChange={e => setBulkStatus(e.target.value as LeadStatus | '')}
              >
                <option value="">Select status…</option>
                {LEAD_STATUSES.map(s => (
                  <option key={s} value={s}>
                    {LEAD_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="ops-modal-footer">
              <button type="button" className="ops-modal-btn secondary" onClick={() => setShowBulkModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="ops-modal-btn primary"
                disabled={!bulkStatus || bulkUpdating}
                onClick={handleBulkApply}
              >
                {bulkUpdating ? 'Updating…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeadsTable;
